/* ============================================
   BYTE-HR-APPLICATION.JS
   HR application tabs + merge into Shopify contact fields (same pattern as franchise).
   ============================================ */

if (!customElements.get('byte-hr-application')) {
  class ByteHrApplication extends HTMLElement {
    #form;
    #boundSubmit;
    #boundTabClick;
    #boundBirthYearInput;

    connectedCallback() {
      this.#form = this.querySelector('form');
      if (!this.#form) return;

      this.#boundSubmit = this.#handleSubmit.bind(this);
      this.#form.addEventListener('submit', this.#boundSubmit);

      this.#boundBirthYearInput = this.#handleBirthYearInput.bind(this);
      this.#form.addEventListener('input', this.#boundBirthYearInput);

      this.#boundTabClick = this.#handleTabClick.bind(this);
      this.addEventListener('click', this.#boundTabClick);

      this.#setActiveTab(this.dataset.activeTab || 'merkez', false);
      this.#loadProvinces();
    }

    disconnectedCallback() {
      this.#form?.removeEventListener('submit', this.#boundSubmit);
      this.#form?.removeEventListener('input', this.#boundBirthYearInput);
      this.removeEventListener('click', this.#boundTabClick);
    }

    #handleBirthYearInput(e) {
      const el = e.target;
      if (!el || el.dataset?.hrField !== 'birth_year') return;
      if (el.tagName !== 'INPUT') return;

      const digits = el.value.replace(/\D/g, '').slice(0, 4);
      if (el.value !== digits) {
        el.value = digits;
      }
    }

    #handleTabClick(e) {
      const btn = e.target.closest('[data-hr-tab]');
      if (!btn) return;
      e.preventDefault();
      const tab = btn.dataset.hrTab;
      if (!tab) return;
      this.#setActiveTab(tab, true);
    }

    #setActiveTab(tab, shouldFocus) {
      this.dataset.activeTab = tab;

      const tabs = this.querySelectorAll('[data-hr-tab]');
      for (const t of tabs) {
        const isSel = t.dataset.hrTab === tab;
        t.setAttribute('aria-selected', isSel ? 'true' : 'false');
        t.tabIndex = isSel ? 0 : -1;
      }

      const panels = this.querySelectorAll('[data-hr-panel]');
      for (const p of panels) {
        const isActive = p.dataset.hrPanel === tab;
        p.hidden = !isActive;
        if (p.tagName === 'FIELDSET') {
          p.disabled = !isActive;
        }
      }

      if (shouldFocus) {
        this.querySelector(`[data-hr-tab="${tab}"]`)?.focus();
      }
    }

    async #loadProvinces() {
      const url = this.dataset.provincesUrl;
      if (!url) return;

      const select = this.querySelector('select[data-hr-field="application_city"]');
      if (!select) return;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Provinces request failed');

        const data = await response.json();
        const keys = Object.keys(data).sort((a, b) => Number(a) - Number(b));

        while (select.options.length > 1) {
          select.remove(1);
        }

        for (const key of keys) {
          const label = data[key];
          if (label == null || label === '') continue;

          const opt = document.createElement('option');
          opt.value = key;
          opt.textContent = label;
          select.appendChild(opt);
        }

        select.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (err) {
        console.error('[byte-hr-application]', err);
      }
    }

    #handleSubmit() {
      const tab = this.dataset.activeTab || 'merkez';
      const panel = this.querySelector(`[data-hr-panel="${tab}"]`);
      if (!panel) return;

      const nameField = this.#form.querySelector('[name="contact[name]"]');
      const emailField = this.#form.querySelector('[name="contact[email]"]');
      const phoneField = this.#form.querySelector('[name="contact[phone]"]');
      const bodyField = this.#form.querySelector('[name="contact[body]"]');

      const fullName = panel.querySelector('[data-hr-field="full_name"]')?.value.trim() ?? '';
      const email = panel.querySelector('[data-hr-field="email"]')?.value.trim() ?? '';
      const phone = panel.querySelector('[data-hr-field="phone"]')?.value.trim() ?? '';

      if (nameField) nameField.value = fullName;
      if (emailField) emailField.value = email;
      if (phoneField) phoneField.value = phone;

      const heading =
        tab === 'merkez' ? this.dataset.bodyHeadingMerkez || '' : this.dataset.bodyHeadingMagaza || '';

      const parts = [];
      if (heading) {
        parts.push(` ${heading}: `);
        parts.push('');
      }

      const skipInBody = new Set(['full_name', 'email', 'phone']);
      const fields = panel.querySelectorAll('[data-hr-field]');

      for (const el of fields) {
        const key = el.dataset.hrField;
        if (!key || skipInBody.has(key)) continue;

        const label = el.dataset.hrLabel || key;
        let raw = '';
        if (el.tagName === 'SELECT') {
          const opt = el.options[el.selectedIndex];
          raw = opt ? opt.text : '';
        } else if ('value' in el) {
          raw = el.value;
        }
        const displayVal = raw.trim() || '—';

        parts.push(`[ ${label} ]`);
        parts.push(displayVal);
        parts.push('');
      }

      if (bodyField) {
        bodyField.value = parts.join('\n').replace(/\n+$/, '');
      }
    }
  }

  customElements.define('byte-hr-application', ByteHrApplication);
}
